import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import { Heading, HStack, Stack, Text, VStack } from '@library';
import ClickableArea from '../../../../core/components/button/ClickableArea';
import type { MetaProduct, RenditionUrl } from '../../../types';
import ProductImage from '../../ProductImage/ProductImage';
import MetaProductDetailsSidePane from '../MetaProductDetailsSidePane';
import styles from './SparePartsSearchBanner.module.scss';
import SkuProductSearch from './SkuProductSearch/SkuProductSearch';
import SparePartsSkuList from './SparePartsSkuList';

const SparePartsSearchBanner = ({
    product,
    searchMode,
}: {
    product: MetaProduct;
    searchMode?: boolean;
}) => {
    const specificationAttributes = product.specificationAttributes ?? [];
    const showMore = specificationAttributes.length > 4;
    const restCount = specificationAttributes.length - 4;

    const [show, setShow] = useState(false);
    return (
        <div
            className={clsx(styles.banner, { [styles.searchMode]: searchMode })}
        >
            {!searchMode && (
                <div className={styles.image}>
                    <ProductImage
                        renditionSize="medium"
                        url={
                            product.images?.[0]?.rendition176pxUrl.replace(
                                '176px',
                                '*'
                            ) as RenditionUrl
                        }
                        overviewPlacement="right"
                    />
                </div>
            )}

            <div className={styles.text}>
                <HStack
                    gap="small"
                    align="center"
                    justify="spaceBetween"
                    wrap={searchMode}
                >
                    <VStack gap="small" justify="center">
                        <Heading h3>
                            {product.manufacturerName} {product.model}
                        </Heading>
                        {specificationAttributes.length > 0 && (
                            <HStack gap="small">
                                {specificationAttributes
                                    .slice(0, 4)
                                    .map(({ value, name }) => (
                                        <Text key={name} secondary regular>
                                            {name}: {value}
                                        </Text>
                                    ))}
                                {showMore && (
                                    <ClickableArea
                                        className={styles.link}
                                        onClick={() => {
                                            setShow(true);
                                        }}
                                    >
                                        <Text secondary regular>
                                            <FormattedMessage
                                                id="+{count} more"
                                                values={{ count: restCount }}
                                            />
                                        </Text>
                                    </ClickableArea>
                                )}
                            </HStack>
                        )}

                        {product.skuList.length > 0 && (
                            <HStack>
                                <Text secondary>
                                    <FormattedMessage
                                        id="article number {count}"
                                        values={{
                                            count: product.skuList.length,
                                        }}
                                    />
                                    :<>&nbsp;</>
                                </Text>
                                <Text secondary>
                                    <HStack gap="small" wrap>
                                        <SparePartsSkuList
                                            limit={10}
                                            skuList={product.skuList}
                                        />
                                    </HStack>
                                </Text>
                            </HStack>
                        )}

                        {searchMode && (
                            <Stack
                                direction="columnMobileWideRow"
                                gap="mini"
                                wrap
                            >
                                <HStack gap="micro">
                                    <div>
                                        <FormattedMessage id="make" />:
                                    </div>
                                    {product.manufacturerName}
                                </HStack>
                                <HStack gap="micro">
                                    <div>
                                        <FormattedMessage id="product type" />:
                                    </div>
                                    {product.type}
                                </HStack>
                                <HStack gap="micro">
                                    <div>
                                        <FormattedMessage id="modelYear" />:
                                    </div>
                                    {product.manufacturingStartYear}
                                </HStack>
                            </Stack>
                        )}
                    </VStack>
                    {searchMode && (
                        <div className={styles.searchModeBlock}>
                            <HStack gap="mini" align="center">
                                <div className={styles.searchModeText}>
                                    <Text primary>
                                        <FormattedMessage id="Product search" />
                                    </Text>
                                </div>

                                <SkuProductSearch
                                    skuMask={product.sparePartSkuPrefix ?? ''}
                                />
                            </HStack>
                        </div>
                    )}
                </HStack>
            </div>

            {show && (
                <MetaProductDetailsSidePane
                    product={product}
                    onClose={() => setShow(false)}
                />
            )}
        </div>
    );
};

export default memo(SparePartsSearchBanner);
